// src/components/UpdateToast.tsx
//
// Pilot-readiness P1 (founder B3, 2026-07-07) — fresh-release delivery.
// The previous setup (vite-pwa `autoUpdate` + injected registration) never
// re-checked for a new service worker while the app stayed open or was
// re-opened from memory on a phone, and the app shell always served from the
// OLD worker's precache — so returning browsers kept running stale builds
// unless the founder used incognito. This component owns the update flow:
//   - registers the SW via `virtual:pwa-register` in prompt mode,
//   - re-checks for a new worker on visibility gain and every 60 s while
//     visible (registration.update() bypasses the HTTP cache for sw.js),
//   - when a new build is waiting, shows a persistent Italian toast; one tap
//     activates the waiting worker and reloads onto the new build.

import { useEffect, useRef, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useTokens } from '../state/theme';

const CHECK_INTERVAL_MS = 60_000;

export function UpdateToast() {
  const tokens = useTokens();
  const [applying, setApplying] = useState(false);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      registrationRef.current = registration ?? null;
    },
  });

  // Re-check on focus/visibility + a slow poll while visible. A registration
  // .update() is cheap (HEAD-ish fetch of sw.js) and is what makes an app that
  // stays open for days finally see the new release.
  useEffect(() => {
    const check = () => {
      if (document.visibilityState === 'visible') {
        void registrationRef.current?.update().catch(() => undefined);
      }
    };
    const id = window.setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', check);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);

  if (!needRefresh) return null;

  return (
    <button
      type="button"
      data-testid="update-toast"
      disabled={applying}
      onClick={() => {
        setApplying(true);
        // true ⇒ skipWaiting + reload once the new worker controls the page.
        void updateServiceWorker(true);
      }}
      style={{
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
        zIndex: 9999,
        background: tokens.accent,
        color: '#fff',
        border: 'none',
        borderRadius: tokens.radius.md,
        padding: '12px 18px',
        minHeight: 44,
        fontFamily: tokens.fonts.body,
        fontSize: 14,
        fontWeight: 600,
        cursor: applying ? 'wait' : 'pointer',
        boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
      }}
    >
      {applying ? 'Aggiorno…' : 'Nuova versione disponibile — tocca per aggiornare'}
    </button>
  );
}
